import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dict, type Lang } from "./i18n";
import { GA } from "./evolution-constants";
import { play, setSoundEnabled, type Cue } from "./sound";
import "./index.css";

type Page = "home" | "brain" | "network" | "evolution" | "quiz";

const STORAGE = {
  lang: "lang",
  page: "page",
  sound: "sound",
  speed: "evo:speed",
  quiz: (lang: Lang) => `quiz:${lang}`,
} as const;

function readPersisted<T>(key: string, parse: (raw: string) => T | null): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    return parse(raw);
  } catch {
    return null;
  }
}

function writePersisted(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* quota / private mode — silently ignore */
  }
}

export function App() {
  const [lang, setLang] = useState<Lang>(() => {
    const saved = readPersisted(STORAGE.lang, (s) => (s === "en" || s === "de" ? s : null));
    if (saved) return saved;
    return navigator.language?.toLowerCase().startsWith("de") ? "de" : "en";
  });
  const [page, setPage] = useState<Page>(() => {
    const saved = readPersisted(STORAGE.page, (s): Page | null =>
      s === "home" || s === "brain" || s === "network" || s === "evolution" || s === "quiz"
        ? s
        : null,
    );
    return saved ?? "home";
  });
  const [soundOn, setSoundOn] = useState<boolean>(() => {
    return readPersisted(STORAGE.sound, (s) => s === "1") ?? false;
  });

  useEffect(() => {
    writePersisted(STORAGE.lang, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    writePersisted(STORAGE.page, page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  useEffect(() => {
    setSoundEnabled(soundOn);
    writePersisted(STORAGE.sound, soundOn ? "1" : "0");
  }, [soundOn]);

  const t = dict[lang];

  // Stable sound trigger — child chapters call this freely.
  const sfx = useCallback((cue: Cue) => play(cue), []);

  const goPage = useCallback(
    (p: Page) => {
      sfx("click");
      setPage(p);
    },
    [sfx],
  );

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="emoji" aria-hidden>
            🧠
          </span>
          <div className="brand-text">
            <span className="brand-name">{t.brand}</span>
            <span className="brand-tagline">{t.tagline}</span>
          </div>
        </div>
        <nav className="nav" aria-label={t.nav.home}>
          {(
            [
              ["home", t.nav.home],
              ["brain", t.nav.brain],
              ["network", t.nav.network],
              ["evolution", t.nav.evolution],
              ["quiz", t.nav.quiz],
            ] as [Page, string][]
          ).map(([id, label]) => (
            <button
              key={id}
              onClick={() => goPage(id)}
              className={page === id ? "active" : ""}
              aria-current={page === id ? "page" : undefined}
            >
              {label}
            </button>
          ))}
          <SoundToggle
            on={soundOn}
            setOn={setSoundOn}
            label={t.sound}
            onText={t.soundOn}
            offText={t.soundOff}
          />
          <LangSwitch lang={lang} setLang={setLang} label={t.lang} />
        </nav>
      </header>

      {page === "home" && <Home t={t} go={goPage} />}
      {page === "brain" && <BrainChapter t={t} go={goPage} sfx={sfx} />}
      {page === "network" && <NetworkChapter t={t} go={goPage} sfx={sfx} />}
      {page === "evolution" && <EvolutionChapter t={t} go={goPage} sfx={sfx} />}
      {page === "quiz" && <QuizChapter t={t} lang={lang} sfx={sfx} />}

      <p className="footer">{t.footer}</p>
    </div>
  );
}

export default App;

/* ------------------------------------------------------------------ */
/* Sound toggle                                                        */
/* ------------------------------------------------------------------ */
function SoundToggle({
  on,
  setOn,
  label,
  onText,
  offText,
}: {
  on: boolean;
  setOn: (v: boolean) => void;
  label: string;
  onText: string;
  offText: string;
}) {
  return (
    <button
      onClick={() => {
        const next = !on;
        setOn(next);
        // Tiny confirmation chirp when turning on.
        setSoundEnabled(next);
        if (next) play("blip");
      }}
      aria-label={label}
      aria-pressed={on}
      title={label}
    >
      {on ? onText : offText}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Language switch                                                     */
/* ------------------------------------------------------------------ */
function LangSwitch({
  lang,
  setLang,
  label,
}: {
  lang: Lang;
  setLang: (l: Lang) => void;
  label: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      style={{ display: "flex", gap: 6, marginLeft: 4 }}
    >
      {(["en", "de"] as Lang[]).map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={lang === l ? "active" : ""}
          aria-pressed={lang === l}
          title={l === "en" ? "English" : "Deutsch"}
        >
          {l === "en" ? "🇬🇧 EN" : "🇩🇪 DE"}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Home                                                                */
/* ------------------------------------------------------------------ */
type T = (typeof dict)["en"];

function Home({ t, go }: { t: T; go: (p: Page) => void }) {
  const targets: readonly Page[] = ["brain", "network", "evolution", "quiz"];
  const items = t.home.chapters
    .map((c, i) => ({ ...c, target: targets[i] }))
    .filter((c): c is typeof c & { target: Page } => c.target !== undefined);
  return (
    <>
      <section className="card hero">
        <h1>
          {t.home.title1} <span className="pop">{t.home.titlePop}</span>{" "}
          {t.home.title2}
        </h1>
        <p>{t.home.intro}</p>
        <div className="btn-row" style={{ justifyContent: "center" }}>
          <button className="btn pink" onClick={() => go("brain")}>
            🚀 {t.home.pickChapter}
          </button>
        </div>
      </section>

      <div className="chapters">
        {items.map((c, i) => (
          <button
            key={i}
            className={`chapter-card c${i + 1}`}
            onClick={() => go(c.target)}
          >
            <span className="badge" aria-hidden>{c.emoji}</span>
            <h3>{c.title}</h3>
            <p>{c.desc}</p>
          </button>
        ))}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Brain chapter — chain of neurons                                    */
/* ------------------------------------------------------------------ */
function BrainChapter({
  t,
  go,
  sfx,
}: {
  t: T;
  go: (p: Page) => void;
  sfx: (cue: Cue) => void;
}) {
  const NEURONS = 5;
  const [active, setActive] = useState<number[]>([]);
  const [bulbOn, setBulbOn] = useState(false);
  const timers = useRef<number[]>([]);

  const reset = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setActive([]);
    setBulbOn(false);
  };

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const fire = (start: number) => {
    if (start !== 0 && !active.includes(start - 1)) return;
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setBulbOn(false);
    const next: number[] = [];
    for (let i = start; i < NEURONS; i++) {
      const idx = i;
      const tt = window.setTimeout(() => {
        next.push(idx);
        setActive((prev) => Array.from(new Set([...prev, idx])));
        sfx("blip");
        if (idx === NEURONS - 1) {
          setBulbOn(true);
          sfx("dingUp");
        }
      }, (i - start) * 380);
      timers.current.push(tt);
    }
  };

  return (
    <section className="card">
      <div className="section-row">
        <div>
          <h2>🧠 {t.brain.title}</h2>
          <p className="subtitle">{t.brain.subtitle}</p>
        </div>
        <button className="btn ghost" onClick={reset}>
          ↺ {t.brain.reset}
        </button>
      </div>

      <p>{t.brain.body1}</p>

      <div className="tip">
        <strong>{t.brain.tryTitle}</strong> {t.brain.tryBody}
      </div>

      <div className="neuron-row" aria-label="neuron chain">
        {Array.from({ length: NEURONS }).map((_, i) => (
          <Neuron
            key={i}
            i={i}
            total={NEURONS}
            on={active.includes(i)}
            edgeOn={active.includes(i) && i < NEURONS - 1}
            onClick={() => fire(i)}
            disabled={i !== 0 && !active.includes(i - 1)}
            hintFirst={i === 0 && active.length === 0}
          />
        ))}
        <span className={`lightbulb ${bulbOn ? "on" : ""}`} aria-hidden>
          💡
        </span>
      </div>

      {!bulbOn && active.length === 0 && (
        <p style={{ textAlign: "center", color: "var(--ink-soft)" }}>
          👉 {t.brain.fireFirst}
        </p>
      )}

      <div className="tip" style={{ background: "#e6f4ff", borderColor: "var(--blue)" }}>
        🤖 {t.brain.didYouKnow}
      </div>

      <div className="btn-row">
        <button className="btn blue" onClick={() => go("network")}>
          {t.brain.next}
        </button>
      </div>
    </section>
  );
}

function Neuron({
  i,
  total,
  on,
  edgeOn,
  onClick,
  disabled,
  hintFirst,
}: {
  i: number;
  total: number;
  on: boolean;
  edgeOn: boolean;
  onClick: () => void;
  disabled: boolean;
  hintFirst: boolean;
}) {
  return (
    <>
      <button
        className={`neuron ${on ? "on" : ""} ${hintFirst ? "hint" : ""}`}
        onClick={onClick}
        disabled={disabled}
        aria-label={`Neuron ${i + 1}`}
        style={{
          opacity: disabled && !on ? 0.55 : 1,
        }}
      >
        {on ? "⚡" : "•"}
      </button>
      {i < total - 1 && <span className={`wire ${edgeOn ? "on" : ""}`} key={`w-${i}-${edgeOn}`} />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Network chapter — Is it a cat?                                      */
/* ------------------------------------------------------------------ */
// Kid-friendly integer "cat points" per feature.
// Whiskers, long tail, meows, has feathers.
const WEIGHTS: readonly number[] = [3, 2, 5, -7];

function NetworkChapter({
  t,
  go,
  sfx,
}: {
  t: T;
  go: (p: Page) => void;
  sfx: (cue: Cue) => void;
}) {
  const [flags, setFlags] = useState<boolean[]>([false, false, false, false]);

  const total = useMemo(
    () => flags.reduce((s, on, i) => (on ? s + (WEIGHTS[i] ?? 0) : s), 0),
    [flags],
  );

  const isCat = total > 0;
  // Sure-o-meter: 50% on the fence, grows toward 100% as |total| gets bigger.
  const verdictConf = Math.min(1, 0.5 + Math.abs(total) / 12);

  // Play a verdict cue only when the cat/not-cat flip flips. Skip the
  // initial render (all flags off) so we don't ding on chapter load.
  const lastVerdictRef = useRef<"none" | "cat" | "no">("none");
  useEffect(() => {
    const next: "none" | "cat" | "no" =
      total === 0 ? "none" : total > 0 ? "cat" : "no";
    if (next !== lastVerdictRef.current && lastVerdictRef.current !== "none") {
      sfx(next === "cat" ? "dingUp" : "dingDown");
    }
    lastVerdictRef.current = next;
  }, [total, sfx]);

  return (
    <section className="card">
      <h2>🤖 {t.network.title}</h2>
      <p className="subtitle">{t.network.subtitle}</p>
      <p>{t.network.body}</p>

      <div className="lab">
        <div>
          <h3 style={{ margin: "10px 0 0" }}>{t.network.inputLabel}</h3>
          <div className="toggle-list">
            {t.network.features.map((f, i) => (
              <label
                key={i}
                className={`toggle ${flags[i] ? "on" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  setFlags((prev) => prev.map((v, j) => (j === i ? !v : v)));
                  sfx("click");
                }}
              >
                <span className="ic" aria-hidden>
                  {f.ic}
                </span>
                <span style={{ flex: 1 }}>{f.label}</span>
                <span className="sw" aria-hidden />
              </label>
            ))}
          </div>
        </div>

        <div>
          <h3 style={{ margin: "10px 0 0" }}>{t.network.outputLabel}</h3>
          <NetSvg
            flags={flags}
            weights={WEIGHTS}
            features={t.network.features}
            labels={{
              input: t.network.layerInput,
              hidden: t.network.layerHidden,
              output: t.network.layerOutput,
            }}
            pointsLabel={t.network.points}
            verdictLabel={isCat ? t.network.catYes : t.network.catNo}
          />

          <div className={`verdict ${isCat ? "yes" : "no"}`}>
            {isCat ? t.network.catYes : t.network.catNo}
          </div>
          <div>
            <small style={{ fontWeight: 800 }}>
              {t.network.confidence}: {Math.round(verdictConf * 100)}%
            </small>
            <div className="confidence-bar" aria-hidden>
              <div style={{ width: `${Math.round(verdictConf * 100)}%` }} />
            </div>
          </div>
        </div>
      </div>

      <Scorecard
        flags={flags}
        weights={WEIGHTS}
        features={t.network.features}
        total={total}
        labels={{
          title: t.network.scoreTitle,
          empty: t.network.scoreEmpty,
          total: t.network.scoreTotal,
          points: t.network.points,
          line: t.network.catLineLabel,
        }}
      />

      <div className="tip" style={{ background: "#eaffe7" }}>
        <strong>{t.network.tipTitle}.</strong> {t.network.tipBody}
      </div>

      <div className="btn-row">
        <button className="btn green" onClick={() => go("evolution")}>
          {t.network.next}
        </button>
      </div>
    </section>
  );
}

function NetSvg({
  flags,
  weights,
  features,
  labels,
  pointsLabel,
  verdictLabel,
}: {
  flags: boolean[];
  weights: readonly number[];
  features: { ic: string; label: string }[];
  labels: { input: string; hidden: string; output: string };
  pointsLabel: string;
  verdictLabel: string;
}) {
  // Layout
  const W = 460;
  const H = 320;
  const inX = 70;
  const hidX = 230;
  const outX = 390;
  const inYs = [60, 130, 200, 270];
  const hidYs = [100, 175, 250];
  const outY = 175;

  const maxAbsW = Math.max(1, ...weights.map((w) => Math.abs(w)));

  // hidden node activations — pseudo, just for visual pulse
  const hidActs = hidYs.map((_, j) => {
    let s = 0;
    flags.forEach((v, i) => {
      if (v) s += (weights[i] ?? 0) * (j === 0 ? 0.15 : j === 1 ? 0.1 : 0.07);
    });
    return Math.max(0, Math.min(1, 0.5 + s));
  });

  const total = flags.reduce(
    (s, on, i) => (on ? s + (weights[i] ?? 0) : s),
    0,
  );
  const outAct = total > 0 ? 1 : 0;

  const edgeColor = (w: number, active: boolean) =>
    !active ? "#cfd3e8" : w >= 0 ? "#5fd07c" : "#ff5a5a";
  const edgeWidth = (w: number, active: boolean) =>
    active ? 1.5 + (Math.abs(w) / maxAbsW) * 4 : 1.5;

  return (
    <svg
      viewBox={`-40 0 ${W + 40} ${H}`}
      className="net-svg"
      role="img"
      aria-label={`Neural network diagram. ${verdictLabel}`}
    >
      {/* edges input -> hidden */}
      {inYs.map((iy, i) =>
        hidYs.map((hy, j) => {
          const active = !!flags[i];
          const w = weights[i] ?? 0;
          return (
            <line
              key={`e1-${i}-${j}`}
              className="net-edge"
              x1={inX + 24}
              y1={iy}
              x2={hidX - 24}
              y2={hy}
              stroke={edgeColor(w, active)}
              strokeWidth={edgeWidth(w, active)}
              opacity={active ? 1 : 0.5}
            />
          );
        }),
      )}
      {/* edges hidden -> output */}
      {hidYs.map((hy, j) => {
        const act = hidActs[j] ?? 0;
        return (
          <line
            key={`e2-${j}`}
            className="net-edge"
            x1={hidX + 24}
            y1={hy}
            x2={outX - 28}
            y2={outY}
            stroke={act > 0.5 ? "#5fd07c" : "#cfd3e8"}
            strokeWidth={2 + act * 4}
            opacity={0.85}
          />
        );
      })}

      {/* input nodes */}
      {inYs.map((iy, i) => {
        const w = weights[i] ?? 0;
        const on = !!flags[i];
        const positive = w >= 0;
        const chipFill = positive ? "#eaffe7" : "#ffecec";
        const chipStroke = positive ? "#1f7a3a" : "#a62525";
        const chipText = positive ? "#1f7a3a" : "#a62525";
        const featLabel = features[i]?.label ?? `Input ${i + 1}`;
        const chipAria = `${featLabel}: ${positive ? "+" : ""}${w} ${pointsLabel}`;
        return (
          <g key={`in-${i}`}>
            {/* point-value chip to the left of the input */}
            <g opacity={on ? 1 : 0.45} role="img" aria-label={chipAria}>
              <title>{chipAria}</title>
              <rect
                x={-32}
                y={iy - 14}
                width={50}
                height={28}
                rx={14}
                ry={14}
                fill={chipFill}
                stroke={chipStroke}
                strokeWidth={2.5}
              />
              <text
                x={-7}
                y={iy + 6}
                textAnchor="middle"
                fontSize="16"
                fontWeight="900"
                fill={chipText}
              >
                {positive ? `+${w}` : `${w}`}
              </text>
            </g>
            <circle
              cx={inX}
              cy={iy}
              r={22}
              className="net-node"
              fill={on ? "#ffd23f" : "#ffffff"}
            />
            <text
              x={inX}
              y={iy + 6}
              textAnchor="middle"
              fontSize="18"
              fontWeight="900"
            >
              {on ? "1" : "0"}
            </text>
          </g>
        );
      })}

      {/* hidden nodes */}
      {hidYs.map((hy, j) => {
        const act = hidActs[j] ?? 0;
        return (
          <circle
            key={`h-${j}`}
            cx={hidX}
            cy={hy}
            r={20 + act * 6}
            className="net-node"
            fill={`hsl(210 90% ${88 - act * 30}%)`}
          />
        );
      })}

      {/* output node */}
      <g role="img" aria-label={verdictLabel}>
        <title>{verdictLabel}</title>
        <circle
          cx={outX}
          cy={outY}
          r={28}
          className="net-node"
          fill={outAct >= 0.5 ? "#5fd07c" : "#ff8a8a"}
        />
        <text
          x={outX}
          y={outY + 8}
          textAnchor="middle"
          fontSize="22"
        >
          {outAct >= 0.5 ? "🐱" : "❌"}
        </text>
      </g>

      {/* layer labels */}
      <text x={inX} y={H - 8} textAnchor="middle" fontSize="12" fill="#4a4f80" fontWeight="800">
        {labels.input}
      </text>
      <text x={hidX} y={H - 8} textAnchor="middle" fontSize="12" fill="#4a4f80" fontWeight="800">
        {labels.hidden}
      </text>
      <text x={outX} y={H - 8} textAnchor="middle" fontSize="12" fill="#4a4f80" fontWeight="800">
        {labels.output}
      </text>
    </svg>
  );
}

function Scorecard({
  flags,
  weights,
  features,
  total,
  labels,
}: {
  flags: boolean[];
  weights: readonly number[];
  features: { ic: string; label: string }[];
  total: number;
  labels: {
    title: string;
    empty: string;
    total: string;
    points: string;
    line: string;
  };
}) {
  const active = flags
    .map((on, i) => ({
      on,
      w: weights[i] ?? 0,
      ic: features[i]?.ic ?? "",
      label: features[i]?.label ?? "",
    }))
    .filter((x) => x.on);

  const sign = (n: number) => (n >= 0 ? `+${n}` : `${n}`);
  const totalColor =
    total > 0 ? "var(--green)" : total < 0 ? "var(--red)" : "var(--ink-soft)";

  return (
    <div className="scorecard">
      <div className="scorecard-head">
        🧮 <strong>{labels.title}</strong>
        <span className="score-line">{labels.line}</span>
      </div>

      {active.length === 0 ? (
        <p className="score-empty">{labels.empty}</p>
      ) : (
        <ul className="score-rows">
          {active.map((x, i) => (
            <li key={i} className="score-row">
              <span className="score-ic" aria-hidden>
                {x.ic}
              </span>
              <span className="score-label">{x.label}</span>
              <span
                className={`score-chip ${x.w >= 0 ? "pos" : "neg"}`}
                aria-label={`${x.label}: ${sign(x.w)} ${labels.points}`}
              >
                {sign(x.w)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="score-total">
        <span>{labels.total}:</span>
        <span className="score-total-num" style={{ color: totalColor }}>
          {sign(total)}
        </span>
        <span className="score-total-unit">{labels.points}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Evolution chapter — genetic algorithm simulation                    */
/* ------------------------------------------------------------------ */
interface Vec2 { x: number; y: number }
interface Bug {
  x: number;
  y: number;
  vx: number;
  vy: number;
  alive: boolean;
  done: boolean;
  hue: number;
  genes: Vec2[]; // sequence of force-vectors (Daniel Shiffman style)
  step: number;
  fitness: number;
  trail: Vec2[];
  elite: boolean;
  rank: number; // 0 = best, only meaningful during showcase
}

function EvolutionChapter({
  t,
  go,
  sfx,
}: {
  t: T;
  go: (p: Page) => void;
  sfx: (cue: Cue) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const popRef = useRef<Bug[]>([]);
  const rafRef = useRef<number | null>(null);
  const frameRef = useRef(0);
  const speedRef = useRef(3);
  const phaseRef = useRef<"running" | "showcase">("running");
  const showcaseUntilRef = useRef(0);
  const ghostRef = useRef<Vec2[]>([]);
  const reachedRef = useRef(0); // for "munch" sfx on new reaches

  const [gen, setGen] = useState(1);
  const [running, setRunning] = useState(false);
  const [bestScore, setBestScore] = useState(0);
  const [alive, setAliveCt] = useState<number>(GA.POP);
  const [reached, setReached] = useState(0);
  const [speedTier, setSpeedTier] = useState<number>(() => {
    const saved = readPersisted(STORAGE.speed, (s) => {
      const n = parseInt(s, 10);
      return Number.isFinite(n) && n >= 1 && n <= 4 ? n : null;
    });
    return saved ?? 2;
  });
  const [banner, setBanner] = useState(false);

  useEffect(() => {
    speedRef.current = speedTier;
    writePersisted(STORAGE.speed, String(speedTier));
  }, [speedTier]);

  const targetRef = useRef({ x: 0, y: 0 });

  // Bugs are born with a *small* innate "food is somewhere right" instinct
  // (positive average x-force). Evolution still has to find the exact path.
  const randomGenes = (n: number): Vec2[] =>
    Array.from({ length: n }, () => ({
      x: GA.BIAS_X + (Math.random() - 0.5) * GA.GENE_HALF_RANGE * 2,
      y: (Math.random() - 0.5) * GA.GENE_HALF_RANGE * 2,
    }));

  const makeBug = (genes: Vec2[], startY: number): Bug => ({
    x: 40,
    y: startY,
    vx: 0,
    vy: 0,
    alive: true,
    done: false,
    hue: Math.random() * 360,
    genes,
    step: 0,
    fitness: 0,
    trail: [],
    elite: false,
    rank: -1,
  });

  const seedPop = (): Bug[] => {
    const c = canvasRef.current;
    const h = c ? c.clientHeight : 360;
    return Array.from({ length: GA.POP }, () =>
      makeBug(randomGenes(GA.GENES), h / 2),
    );
  };

  const reset = () => {
    setGen(1);
    setBestScore(0);
    setAliveCt(GA.POP);
    setReached(0);
    setBanner(false);
    frameRef.current = 0;
    reachedRef.current = 0;
    phaseRef.current = "running";
    showcaseUntilRef.current = 0;
    ghostRef.current = [];
    popRef.current = seedPop();
    draw();
  };

  // initialize once
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const resize = () => {
      const r = c.getBoundingClientRect();
      c.width = Math.floor(r.width * devicePixelRatio);
      c.height = Math.floor(r.height * devicePixelRatio);
      const h = c.clientHeight;
      targetRef.current = { x: c.clientWidth - 50, y: h / 2 };
      draw();
    };
    resize();
    window.addEventListener("resize", resize);

    popRef.current = seedPop();
    const h = c.clientHeight;
    targetRef.current = { x: c.clientWidth - 50, y: h / 2 };
    draw();
    return () => window.removeEventListener("resize", resize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // animation loop
  useEffect(() => {
    if (!running) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    const tick = () => {
      step();
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const step = () => {
    const c = canvasRef.current;
    if (!c) return;
    const W = c.clientWidth;
    const H = c.clientHeight;
    targetRef.current = { x: W - 50, y: H / 2 };

    // showcase phase: just wait, then breed
    if (phaseRef.current === "showcase") {
      if (performance.now() >= showcaseUntilRef.current) {
        breedNext(W, H);
      }
      draw();
      return;
    }

    const iters = speedRef.current;
    for (let it = 0; it < iters; it++) {
      const pop = popRef.current;
      for (const b of pop) {
        if (!b.alive || b.done) continue;
        const g = b.genes[Math.min(b.step, b.genes.length - 1)] ?? { x: 0, y: 0 };
        // accelerate by force, then damp
        b.vx = (b.vx + g.x * GA.FORCE) * GA.DAMPING;
        b.vy = (b.vy + g.y * GA.FORCE) * GA.DAMPING;
        // clamp speed
        const sp = Math.hypot(b.vx, b.vy);
        if (sp > GA.MAX_SPEED) {
          b.vx = (b.vx / sp) * GA.MAX_SPEED;
          b.vy = (b.vy / sp) * GA.MAX_SPEED;
        }
        b.x += b.vx;
        b.y += b.vy;
        b.step++;

        // trail — keep the full path from start so the kid can see the
        // whole route each bug took (and how it inherits across gens).
        b.trail.push({ x: b.x, y: b.y });

        // walls kill — survival of the fittest is part of the demo.
        if (b.x < 6 || b.x > W - 6 || b.y < 6 || b.y > H - 6) {
          b.alive = false;
        }
        // reached target
        const dx = targetRef.current.x - b.x;
        const dy = targetRef.current.y - b.y;
        if (dx * dx + dy * dy < GA.TARGET_R * GA.TARGET_R) {
          b.done = true;
        }
      }
      frameRef.current++;

      const allFinished = popRef.current.every((b) => !b.alive || b.done);
      if (allFinished || frameRef.current >= GA.MAX_STEPS) {
        enterShowcase(W);
        break;
      }
    }
    const aliveCount = popRef.current.filter((b) => b.alive && !b.done).length;
    const doneCount = popRef.current.filter((b) => b.done).length;
    setAliveCt(aliveCount);
    if (doneCount > reachedRef.current) {
      // first new carrot reach this generation — chomp!
      sfx("munch");
    }
    reachedRef.current = doneCount;
    setReached(doneCount);
    draw();
  };

  const enterShowcase = (W: number) => {
    const pop = popRef.current;
    const tgt = targetRef.current;
    pop.forEach((b) => {
      const dx = tgt.x - b.x;
      const dy = tgt.y - b.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      // Squared progress so closer-to-carrot bugs dominate breeding.
      const prog = Math.max(0, (W - dist) / W);
      let fit = prog * prog;
      if (b.done) {
        // huge bonus + extra reward for getting there *fast*
        fit = 4 + Math.max(0, (GA.MAX_STEPS - b.step) / GA.MAX_STEPS) * 2;
      }
      b.fitness = Math.max(0.0001, fit);
    });
    // rank & mark elites
    const sorted = [...pop].sort((a, b) => b.fitness - a.fitness);
    sorted.forEach((b, i) => {
      b.rank = i;
      b.elite = i < GA.ELITE_COUNT;
    });
    const best = sorted[0]?.fitness ?? 0;
    setBestScore((prev) => Math.max(prev, best));
    // capture champion's trail for the ghost in the NEXT generation
    if (sorted[0]) ghostRef.current = sorted[0].trail.slice();

    phaseRef.current = "showcase";
    showcaseUntilRef.current = performance.now() + GA.SHOWCASE_MS;
    setBanner(true);
  };

  const breedNext = (_W: number, H: number) => {
    const pop = popRef.current;

    // Rank-based selection: only the top TOP_FRAC breed. Bias toward the
    // very best by squaring the uniform pick (Math.random() ** 2 favors
    // small indices).
    const sorted = [...pop].sort((a, b) => b.fitness - a.fitness);
    const topK = Math.max(3, Math.floor(GA.POP * GA.TOP_FRAC));
    const pool = sorted.slice(0, topK);
    const pickParent = (): Bug => {
      const idx = Math.floor(Math.random() ** 2 * pool.length);
      return pool[idx] ?? pool[0]!;
    };

    // Keep a couple of top bugs unchanged (memory of best DNA so far).
    const next: Bug[] = [];
    for (const e of sorted.slice(0, GA.ELITE_KEEP)) {
      next.push(makeBug([...e.genes], H / 2));
    }
    // Inject fresh-random "immigrant" bugs every generation. These are the
    // visible mutation outliers — they obviously break away from the swarm.
    for (let i = 0; i < GA.IMMIGRANTS && next.length < GA.POP; i++) {
      next.push(makeBug(randomGenes(GA.GENES), H / 2));
    }

    while (next.length < GA.POP) {
      const a = pickParent();
      const b = pickParent();
      const cut = Math.floor(Math.random() * GA.GENES);
      const genes: Vec2[] = a.genes
        .slice(0, cut)
        .concat(b.genes.slice(cut))
        .map((g) => ({ ...g }));
      // Drift mutation: nudge a few genes by a small random amount instead of
      // erasing them. This converges much faster than fully-random mutation.
      for (const gg of genes) {
        if (Math.random() < GA.MUT_RATE) {
          gg.x += (Math.random() - 0.5) * GA.MUT_DRIFT;
          gg.y += (Math.random() - 0.5) * GA.MUT_DRIFT;
          // very rarely, a big jump (keeps diversity alive)
          if (Math.random() < GA.BIG_JUMP) {
            gg.x = GA.BIAS_X + (Math.random() - 0.5) * GA.GENE_HALF_RANGE * 2;
            gg.y = (Math.random() - 0.5) * GA.GENE_HALF_RANGE * 2;
          }
        }
      }
      next.push(makeBug(genes, H / 2));
    }
    popRef.current = next;
    frameRef.current = 0;
    reachedRef.current = 0;
    phaseRef.current = "running";
    setBanner(false);
    setGen((g) => g + 1);
    setAliveCt(GA.POP);
    setReached(0);
  };

  // --- draw helpers --------------------------------------------------
  const drawBug = (
    ctx: CanvasRenderingContext2D,
    b: Bug,
    showcase: boolean,
  ) => {
    const angle = Math.atan2(b.vy, b.vx);
    const dim = !b.alive && !b.done;
    const elite = showcase && b.elite;
    const bodyA = `hsl(${b.hue} 75% ${dim ? 70 : 50}%)`;
    const bodyB = `hsl(${b.hue} 75% ${dim ? 55 : 30}%)`;

    // halo for elites
    if (elite) {
      ctx.save();
      ctx.translate(b.x, b.y);
      const pulse = 1 + Math.sin(performance.now() / 180) * 0.12;
      ctx.fillStyle = "rgba(255, 210, 63, 0.45)";
      ctx.beginPath();
      ctx.arc(0, 0, 16 * pulse, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    ctx.save();
    ctx.translate(b.x, b.y);
    if (!Number.isNaN(angle)) ctx.rotate(angle);

    // legs
    ctx.strokeStyle = bodyB;
    ctx.lineWidth = 1.6;
    ctx.lineCap = "round";
    for (let i = -1; i <= 1; i++) {
      const lx = i * 3;
      ctx.beginPath();
      ctx.moveTo(lx, -4);
      ctx.lineTo(lx - 1, -9);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(lx, 4);
      ctx.lineTo(lx - 1, 9);
      ctx.stroke();
    }
    // antennae
    ctx.beginPath();
    ctx.moveTo(6, -2);
    ctx.lineTo(11, -6);
    ctx.moveTo(6, 2);
    ctx.lineTo(11, 6);
    ctx.stroke();

    // body (oval)
    ctx.fillStyle = dim ? "rgba(140,140,150,0.55)" : bodyA;
    ctx.strokeStyle = "#1f2350";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.ellipse(0, 0, 10, 6.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // wing crease down the middle
    ctx.beginPath();
    ctx.moveTo(-6, 0);
    ctx.lineTo(8, 0);
    ctx.stroke();

    // spots
    ctx.fillStyle = bodyB;
    for (const [sx, sy] of [
      [-3, -2.5],
      [-3, 2.5],
      [2, -2],
      [2, 2],
    ] as [number, number][]) {
      ctx.beginPath();
      ctx.arc(sx, sy, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }

    // head
    ctx.fillStyle = "#1f2350";
    ctx.beginPath();
    ctx.ellipse(7, 0, 3.2, 4, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();

    // crown floats above (unrotated)
    if (elite) {
      ctx.font = "16px serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("👑", b.x, b.y - 18);
    }
    // green check on bugs that reached carrot
    if (b.done && !showcase) {
      ctx.font = "12px serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("✨", b.x, b.y - 14);
    }
  };

  const draw = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const W = c.clientWidth;
    const H = c.clientHeight;
    const showcase = phaseRef.current === "showcase";
    ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

    // sky/ground gradient
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, "#dff1ff");
    grad.addColorStop(0.75, "#fff4b8");
    grad.addColorStop(1, "#e6f8c8");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // start zone
    ctx.fillStyle = "rgba(31,35,80,0.10)";
    ctx.fillRect(0, 0, 22, H);
    ctx.fillStyle = "#4a4f80";
    ctx.font = "11px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.save();
    ctx.translate(11, H / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText("START", 0, 0);
    ctx.restore();

    // ghost trail of previous champion
    if (ghostRef.current.length > 1) {
      ctx.strokeStyle = "rgba(157, 107, 255, 0.55)";
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      const p0 = ghostRef.current[0]!;
      ctx.moveTo(p0.x, p0.y);
      for (let i = 1; i < ghostRef.current.length; i++) {
        const p = ghostRef.current[i]!;
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // target carrot with pulse during showcase
    const tx = W - 50;
    const ty = H / 2;
    ctx.font = `${showcase ? 44 : 38}px serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("🥕", tx, ty);

    // bugs: draw non-elites first, elites on top
    const pop = popRef.current;
    const order = showcase
      ? [...pop].sort((a, b) => Number(a.elite) - Number(b.elite))
      : pop;

    // trails
    for (const b of order) {
      if (b.trail.length < 2) continue;
      ctx.strokeStyle = b.done
        ? "rgba(95,208,124,0.6)"
        : !b.alive
        ? "rgba(120,120,120,0.25)"
        : `hsla(${b.hue}, 80%, 55%, 0.55)`;
      ctx.lineWidth = b.elite && showcase ? 2.5 : 1.5;
      ctx.beginPath();
      const t0 = b.trail[0]!;
      ctx.moveTo(t0.x, t0.y);
      for (let i = 1; i < b.trail.length; i++) {
        const p = b.trail[i]!;
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
    }

    for (const b of order) drawBug(ctx, b, showcase);
  };

  const changeSpeed = (delta: number) => {
    const next = Math.max(1, Math.min(4, speedTier + delta));
    setSpeedTier(next);
  };

  return (
    <section className="card">
      <h2>🐛 {t.evolution.title}</h2>
      <p className="subtitle">{t.evolution.subtitle}</p>
      <p>{t.evolution.body}</p>

      <div className="sim-wrap">
        <canvas ref={canvasRef} />
        {banner && <div className="sim-banner">{t.evolution.banner}</div>}
        {gen > 1 && (
          <div className="sim-legend" aria-hidden>
            <span className="legend-line" /> {t.evolution.ghostLabel}
          </div>
        )}
      </div>

      <div className="sim-stats">
        <span className="stat">
          {t.evolution.generation}: <strong>{gen}</strong>
        </span>
        <span className="stat">
          {t.evolution.alive}: <strong>{alive}</strong>
        </span>
        <span className="stat">
          {t.evolution.reached}: <strong>{reached}</strong>
        </span>
        <span className="stat">
          {t.evolution.best}: <strong>{bestScore.toFixed(2)}</strong>
        </span>
        <span className="stat">⚡ x{speedTier}</span>
      </div>

      <div className="btn-row">
        <button
          className="btn green"
          onClick={() => setRunning((r) => !r)}
        >
          {running ? `⏸ ${t.evolution.pause}` : `▶ ${t.evolution.start}`}
        </button>
        <button className="btn ghost" onClick={reset}>
          ↺ {t.evolution.reset}
        </button>
        <button className="btn ghost" onClick={() => changeSpeed(-1)}>
          🐢 {t.evolution.slower}
        </button>
        <button className="btn ghost" onClick={() => changeSpeed(1)}>
          🐇 {t.evolution.faster}
        </button>
      </div>

      <div className="tip" style={{ background: "#e6f4ff" }}>
        <strong>{t.evolution.familyTitle}</strong> {t.evolution.familyBody}
      </div>

      <div className="tip" style={{ background: "#fff0f8" }}>
        <strong>{t.evolution.darwinTitle}</strong> {t.evolution.darwinBody}
      </div>

      <div className="btn-row">
        <button className="btn purple" onClick={() => go("quiz")}>
          {t.evolution.next}
        </button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Quiz                                                                */
/* ------------------------------------------------------------------ */
function QuizChapter({
  t,
  lang,
  sfx,
}: {
  t: T;
  lang: Lang;
  sfx: (cue: Cue) => void;
}) {
  const total = t.quiz.questions.length;

  const loadAnswers = useCallback(
    (l: Lang, count: number): (number | null)[] => {
      const raw = readPersisted(STORAGE.quiz(l), (s) => {
        try {
          const parsed = JSON.parse(s) as unknown;
          if (!Array.isArray(parsed)) return null;
          if (parsed.length !== count) return null;
          return parsed.map((v) =>
            typeof v === "number" || v === null ? (v as number | null) : null,
          );
        } catch {
          return null;
        }
      });
      return raw ?? Array.from({ length: count }, () => null);
    },
    [],
  );

  const [picked, setPicked] = useState<(number | null)[]>(() =>
    loadAnswers(lang, total),
  );

  // re-hydrate when language changes (each lang has its own answer history)
  useEffect(() => {
    setPicked(loadAnswers(lang, total));
  }, [lang, total, loadAnswers]);

  // persist as user answers
  useEffect(() => {
    writePersisted(STORAGE.quiz(lang), JSON.stringify(picked));
  }, [picked, lang]);

  const score = picked.reduce<number>(
    (s, p, i) => (p !== null && p === t.quiz.questions[i]?.a ? s + 1 : s),
    0,
  );
  const allAnswered = picked.every((p) => p !== null);

  return (
    <section className="card">
      <h2>❓ {t.quiz.title}</h2>
      <p className="subtitle">{t.quiz.subtitle}</p>

      {t.quiz.questions.map((q, qi) => (
        <div
          key={qi}
          style={{
            marginTop: 18,
            paddingTop: 14,
            borderTop: qi === 0 ? "none" : "2px dashed #d6d9ee",
          }}
        >
          <p className="quiz-q">
            {qi + 1}. {q.q}
          </p>
          <div className="quiz-options" role="radiogroup" aria-label={q.q}>
            {q.options.map((opt, oi) => {
              const pickedAns = picked[qi];
              const answered = pickedAns !== null;
              const isPicked = pickedAns === oi;
              const isCorrect = answered && oi === q.a;
              const isWrong = isPicked && oi !== q.a;
              const isLocked = answered && !isPicked && !isCorrect;
              return (
                <button
                  key={oi}
                  className={`quiz-option ${
                    isCorrect ? "correct" : isWrong ? "wrong" : isLocked ? "locked" : ""
                  }`}
                  disabled={answered}
                  aria-checked={isPicked}
                  role="radio"
                  onClick={() => {
                    const correct = oi === q.a;
                    sfx(correct ? "correct" : "wrong");
                    setPicked((prev) => {
                      const c = [...prev];
                      c[qi] = oi;
                      return c;
                    });
                  }}
                >
                  <span className="quiz-option-text">{opt}</span>
                  {isCorrect && <span className="quiz-mark" aria-hidden>✓</span>}
                  {isWrong && <span className="quiz-mark" aria-hidden>✕</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="quiz-score">
        {t.quiz.score} {score} / {total} {allAnswered && score === total && "🎉"}
      </div>

      <div className="btn-row">
        <button
          className="btn ghost"
          onClick={() => {
            setPicked(t.quiz.questions.map(() => null));
            sfx("click");
          }}
        >
          ↺ {t.quiz.retry}
        </button>
      </div>
    </section>
  );
}
